'use client'

/**
 * Container Section
 * Wrapper component for nested sections with layout and background support
 */

import React from 'react'

import { sectionSchemaRegistry } from '@/lib/section-schema-registry'

import { BackgroundContainer } from './BackgroundContainer'

import type { BackgroundValue, ContainerValue, LayoutValue, SectionElementSchema } from '@/types/element-schema.type'

export interface ContainerSectionProps {
  /** Container configuration */
  container?: ContainerValue
  /** Layout configuration */
  layout?: LayoutValue
  /** Background configuration */
  background?: BackgroundValue
  /** Child components to render */
  children?: React.ReactNode
  /** Additional CSS classes */
  className?: string
  /** Section ID for anchor links */
  id?: string
}

/**
 * Get Tailwind classes from layout configuration
 */
function getLayoutClasses(layout?: LayoutValue): string {
  if (!layout) return ''

  const classes: string[] = []

  // Columns
  if (layout.columns && layout.columns > 1) {
    classes.push(`grid grid-cols-1 md:grid-cols-${layout.columns}`)
  }

  // Gap
  if (layout.gap) {
    classes.push(layout.gap)
  }

  // Direction
  if (layout.direction === 'column') {
    classes.push('flex flex-col')
  } else if (layout.direction === 'row' || layout.columns) {
    classes.push('flex flex-row')
  }

  // Alignment
  if (layout.align) {
    const alignMap: Record<string, string> = {
      left: 'items-start',
      center: 'items-center',
      right: 'items-end',
      stretch: 'items-stretch',
    }
    classes.push(alignMap[layout.align] || '')
  }

  // Justify
  if (layout.justify) {
    const justifyMap: Record<string, string> = {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
      around: 'justify-around',
    }
    classes.push(justifyMap[layout.justify] || '')
  }

  // Wrap
  if (layout.wrap) {
    classes.push('flex-wrap')
  }

  return classes.filter(Boolean).join(' ')
}

/**
 * Get container classes from configuration
 */
function getContainerClasses(container?: ContainerValue): string {
  const classes: string[] = []

  if (container?.maxWidth) {
    classes.push(container.maxWidth)
  }

  if (container?.padding) {
    classes.push(container.padding)
  }

  if (container?.margin) {
    classes.push(container.margin)
  }

  return classes.filter(Boolean).join(' ')
}

/**
 * Container Section Component
 * Provides a flexible wrapper for nested sections
 */
export function ContainerSection({
  container,
  layout,
  background,
  children,
  className = '',
  id,
}: ContainerSectionProps) {
  const layoutClasses = getLayoutClasses(layout)
  const containerClasses = getContainerClasses(container)
  const combinedClasses = `${containerClasses} ${layoutClasses} ${className}`.trim()

  return (
    <BackgroundContainer background={background} className={combinedClasses}>
      <section id={id} className="w-full">
        {children}
      </section>
    </BackgroundContainer>
  )
}

/**
 * Container Section with default props for Puck
 */
export const ContainerSectionDefaults = {
  container: {
    children: [],
    maxWidth: 'max-w-7xl',
    padding: 'py-16 px-4',
    margin: '',
  },
  layout: {
    columns: 1,
    gap: 'gap-8',
    align: 'center',
    direction: 'column',
    wrap: false,
  },
  background: {
    type: 'none',
  },
  id: '',
  className: '',
}

/**
 * Element schema for ContainerSection
 * Used by Puck to generate edit fields
 */
export const containerSectionSchema: SectionElementSchema = {
  componentType: 'ContainerSection',
  label: 'Container Section',
  description: 'A flexible container for nested sections with layout and background options',
  category: 'layout',
  tags: ['layout', 'wrapper'],
  elements: [
    {
      key: 'id',
      type: 'text',
      label: 'Section ID',
      helpText: 'Unique identifier for anchor links',
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Classes',
    },
    {
      key: 'layout',
      type: 'layout',
      label: 'Layout',
      default: {
        columns: 1,
        gap: 'gap-8',
        align: 'center',
        direction: 'column',
      },
    },
    {
      key: 'background',
      type: 'background',
      label: 'Background',
      default: { type: 'none' },
    },
  ],
}

// Register schema
sectionSchemaRegistry.register(containerSectionSchema)

export default ContainerSection
